import { PLATFORM, type Platform } from "../../lib/platform.ts";

import { CHAT_MODE_ENABLED, type WorkspaceMode } from "../../app/workspace.ts";
import { SECTIONS, type DecisionKind, type SectionKey } from "./types.ts";

/**
 * EL MAPA DE ATAJOS de la digitación. Un solo sitio.
 *
 * De aquí salen la chuleta, las insignias de los botones, la paleta de
 * comandos y lo que hace cada tecla. Cambiar un atajo es cambiarlo aquí.
 *
 * La gramática copia la de Omarchy --una tecla base, letra con sentido, Shift
 * cambia el verbo, números para saltar directo-- con una diferencia forzada:
 * la tecla base es **Alt** y no Super, porque Super la toma el sistema
 * operativo. Ni Ctrl (Ctrl+W/T/N cierran y abren pestañas, y Ctrl a secas es
 * de editar texto), ni Ctrl+Alt (en los teclados en español es AltGr y
 * escribe @ o #).
 *
 * - **Alt + letra** hace algo; la letra es la inicial de lo que hace (T, toda
 *   la foto; R, recuadros; P, pantalla completa; S, siguiente; N, nueva
 *   línea), salvo K y J, que son las de Omarchy (atajos y cambiar la
 *   división), Z, deshacer, la de todos los editores (con Shift,
 *   rehacer), e Y, girar la foto (con Shift, al revés): ninguna letra
 *   libre empieza como «girar» o «rotar».
 * - **Enter** confirma lo elegido y pasa al siguiente documento. Vale sobre
 *   «Confirmar» y en
 *   cualquier sitio que no atienda Enter por su cuenta (`free`): así un clic
 *   en la foto o en un hueco no lo apaga. Dentro de un campo, Enter acepta
 *   el dato y avanza; en un botón lo pulsa; en la lista abre el documento.
 *   Nunca confirmal documento dejando la tecla apretada.
 * - **Alt + Enter** toma el siguiente documento: es la acción principal, como
 *   Super+Enter abre la terminal en Omarchy. Con Shift, la suelta.
 * - **Alt + número**: vas tú. 0 es la cola; 1 y 2 los dos recortes, en el
 *   orden en que se leen.
 * - **Alt + Shift + número**: val documento. 1 Válida, 2 Inválida, 3 Para
 *   información; como Super+Shift+número manda la ventana a un escritorio.
 * - **Shift** con una letra es la otra cara del mismo gesto: de uno a los
 *   tres recortes (T), de siguiente a anterior (S), de agregar a quitar una
 *   línea (N), de pantalla completa a ancho completo (P).
 *
 * Lo que NO se usa, a propósito: Alt+D, Alt+E, Alt+F y Alt+Espacio (el menú
 * del navegador y el de la ventana), Alt+← y Alt+→ (atrás y adelante), Alt+4
 * a Alt+9 (pestañas en Linux), Ctrl+W/T/N/L/Tab, F5, F10, F11 (la pantalla
 * completa del navegador sigue siendo suya, y la pantalla la nota) y Super. Alt+W
 * tampoco, aunque en Omarchy cierre: acostumbrar la mano a «W cierra» es un
 * Ctrl+W por error, que cierra la pestaña con el trabajo dentro.
 *
 * **En Mac, la misma gramática con otras teclas** (`fromMac`, `keysFor`). Un
 * solo mapa, escrito como en un PC, y una traducción a la entrada y otra a
 * las insignias:
 *
 * - La tecla base es **⌃ Control**, no ⌥. En Mac, ⌥ es lo que AltGr en
 *   Windows: escribe. Con el teclado U.S. la ñ es ⌥N y luego N (tecla
 *   muerta, cuya composición ningún navegador deja cancelar); en Español,
 *   ⌥1, ⌥2 y ⌥3 son |, @ y #. Y lo que en Windows hace de Ctrl --editar,
 *   las pestañas-- en Mac lo hace ⌘, así que ⌃ queda libre: ⌃+letra no
 *   escribe con ninguna distribución.
 * - **⌥↑ y ⌥↓** mueven el foco: ⌃↑ es Mission Control y ⌃↓ las ventanas de
 *   la app, y nunca llegan a la página. Las flechas no escriben, y en Mac ⌥
 *   con flechas ya es «saltar más lejos».
 * - Lo de **Ctrl** en un PC --guardar, el menú, deshacer-- es **⌘** en Mac,
 *   como lo espera la mano: ⌘↩, ⌘K, ⌘Z y ⇧⌘Z.
 * - Cualquier otro ⌥ no es atajo: escribe, como en cualquier app de la Mac.
 *   ⌃⌥ tampoco: es VoiceOver y el `accesskey` de Chrome y Safari.
 *
 * Lo que se pierde: dentro de los campos de esta pantalla, los atajos de
 * edición de macOS que caen en letras del mapa (⌃A, B, N, P, K, T, O). Y si
 * alguien encendió «Cambiar al escritorio N» (⌃1…, apagado de partida),
 * ⌃0 a ⌃3 se los queda el sistema: quedan el menú de comandos y los botones.
 */

/** Lo que puede pedir una tecla. Quien la ejecuta es la pantalla. */
export type Command =
  | { readonly type: "save" }
  | {
      readonly type: "decide";
      readonly kind: DecisionKind;
      /** Con Shift: elige Y guarda, y pasa a la siguiente. */
      readonly apply: boolean;
    }
  | { readonly type: "goToCrop"; readonly section: SectionKey }
  | { readonly type: "goToQueue" }
  | { readonly type: "focus"; readonly direction: "up" | "down" }
  | { readonly type: "invoice"; readonly step: 1 | -1 }
  | { readonly type: "takeNext" }
  /** Volver a la cola: cerrar el documento abierto (y soltarla si es tuya). */
  | { readonly type: "closeInvoice" }
  | { readonly type: "release" }
  /** Deshacer o rehacer lo último que se guardó: decidir, agregar o quitar una línea. */
  | { readonly type: "undo" }
  | { readonly type: "redo" }
  | { readonly type: "search" }
  | { readonly type: "addLine" }
  | { readonly type: "removeLine" }
  /** Pliega o despliega «No participan» / «Otros ítems». */
  | { readonly type: "toggleNotProduct" }
  /** Baja la línea con el foco a «No participa» (se guarda al momento). */
  | { readonly type: "demoteLine" }
  | { readonly type: "batch" }
  | { readonly type: "attention"; readonly step: 1 | -1 }
  /** Gira la foto un cuarto de vuelta: horario (1) o antihorario (-1). */
  | { readonly type: "rotatePhoto"; readonly direction: 1 | -1 }
  | { readonly type: "toggleFull"; readonly all: boolean }
  | { readonly type: "toggleSplit" }
  | { readonly type: "toggleBoxes" }
  | { readonly type: "zoom"; readonly direction: 1 | -1 }
  | { readonly type: "original" }
  | { readonly type: "fullscreen" }
  | { readonly type: "fullWidth" }
  | { readonly type: "palette" }
  | { readonly type: "shortcuts" }
  /** Contarle algo a Alfred: B de buzón, el que era. */
  | { readonly type: "alfred" }
  /** Ir a los pedidos a Alfred: sin tecla propia, en el menú. */
  | { readonly type: "alfredList" }
  | { readonly type: "mode"; readonly mode: WorkspaceMode };

/** Lo que importa de una pulsación. Un `KeyboardEvent` lo cumple. */
export interface KeyPress {
  readonly key: string;
  readonly code: string;
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly metaKey: boolean;
  /** La tecla se quedó apretada: el navegador la repite. */
  readonly repeat?: boolean;
}

/**
 * El número pulsado, por la TECLA y no por el carácter: con Shift, en un
 * teclado en español, el 1 escribe «!» y el 3 «#».
 */
export const digitOf = (press: KeyPress): number | null => {
  const match = /^(?:Digit|Numpad)(\d)$/.exec(press.code);
  return match === null ? null : Number(match[1]);
};

const DECISION_BY_DIGIT: Record<number, DecisionKind> = {
  1: "valid",
  2: "invalid",
  3: "forData",
};

const isPlusKey = (press: KeyPress) =>
  press.key === "+" || press.key === "=" || press.code === "NumpadAdd";
const isMinusKey = (press: KeyPress) =>
  press.key === "-" || press.code === "NumpadSubtract";

const isVertical = (press: KeyPress) =>
  press.code === "ArrowUp" || press.code === "ArrowDown";

/**
 * La pulsación de una Mac, dicha como la de un PC, para que la entienda el
 * mismo mapa: ⌃ es la tecla base (Alt), ⌘ la de las órdenes (Ctrl) y ⌥ solo
 * vale con ↑ y ↓. Lo que no tiene traducción --⌥ con cualquier otra tecla,
 * dos modificadores a la vez, ⌃↑ y ⌃↓-- no es un atajo: `null`.
 */
export const fromMac = (press: KeyPress): KeyPress | null => {
  const { altKey: option, ctrlKey: control, metaKey: command } = press;
  const held = [option, control, command].filter(Boolean).length;
  if (held === 0) return press;
  if (held > 1) return null;
  /* Campo por campo, y no `{ ...press }`: en un `KeyboardEvent` son
     getters del prototipo y el spread no copia ninguno. */
  const as = (modifier: "altKey" | "ctrlKey"): KeyPress => ({
    key: press.key,
    code: press.code,
    shiftKey: press.shiftKey,
    repeat: press.repeat === true,
    altKey: modifier === "altKey",
    ctrlKey: modifier === "ctrlKey",
    metaKey: false,
  });
  if (control) return isVertical(press) ? null : as("altKey");
  if (command) return as("ctrlKey");
  return isVertical(press) ? press : null;
};

/**
 * Lo que pide una pulsación EN CUALQUIER SITIO de la pantalla, también
 * escribiendo en un campo. Las teclas sueltas no están aquí: las atiende cada
 * panel, y solo cuando no se está escribiendo.
 *
 * `free`: el foco está donde nadie atiende Enter --el fondo, un texto, un
 * hueco--: ni un campo, ni un botón, ni la lista. Ahí Enter confirma.
 *
 * Super nunca: es del sistema operativo. AltGr --que Windows manda como
 * Ctrl+Alt-- tampoco: es para escribir @, # o €. En Mac, la pulsación se
 * traduce antes (`fromMac`).
 */
export const commandOf = (
  pressed: KeyPress,
  editing: boolean,
  free = false,
  platform: Platform = PLATFORM,
): Command | null => {
  const press = platform === "mac" ? fromMac(pressed) : pressed;
  if (press === null || press.metaKey) return null;
  const { altKey: alt, ctrlKey: ctrl, shiftKey: shift } = press;

  if (ctrl && !alt) {
    if (press.key === "Enter" && !shift) return { type: "save" };
    if (press.code === "KeyK" && !shift) return { type: "palette" };
    /* Dentro de un campo, Ctrl+Z (⌘Z en Mac) es del campo: deshace lo
       escrito. */
    if (press.code === "KeyZ" && !editing) {
      return shift ? { type: "redo" } : { type: "undo" };
    }
    if (press.key === "/" || press.code === "NumpadDivide") {
      return { type: "shortcuts" };
    }
    return null;
  }

  if (alt && !ctrl) {
    const digit = digitOf(press);
    if (digit !== null) {
      if (shift) {
        const kind = DECISION_BY_DIGIT[digit];
        return kind === undefined
          ? null
          : { type: "decide", kind, apply: true };
      }
      if (digit === 0) return { type: "goToQueue" };
      const section = SECTIONS[digit - 1];
      return section === undefined ? null : { type: "goToCrop", section };
    }
    if (press.key === "Enter") {
      return shift ? { type: "release" } : { type: "takeNext" };
    }
    if (isPlusKey(press)) return { type: "zoom", direction: 1 };
    if (isMinusKey(press)) return { type: "zoom", direction: -1 };
    switch (press.code) {
      case "ArrowUp":
        return shift ? null : { type: "focus", direction: "up" };
      case "ArrowDown":
        return shift ? null : { type: "focus", direction: "down" };
      case "KeyS":
        return { type: "invoice", step: shift ? -1 : 1 };
      case "KeyN":
        return shift ? { type: "removeLine" } : { type: "addLine" };
      case "KeyC":
        return shift ? { type: "demoteLine" } : { type: "toggleNotProduct" };
      case "KeyA":
        return { type: "attention", step: shift ? -1 : 1 };
      case "KeyL":
        return shift ? null : { type: "batch" };
      case "KeyT":
        return { type: "toggleFull", all: shift };
      case "KeyJ":
        return shift ? null : { type: "toggleSplit" };
      case "KeyR":
        return shift ? null : { type: "toggleBoxes" };
      case "KeyO":
        return shift ? null : { type: "original" };
      case "KeyP":
        return shift ? { type: "fullWidth" } : { type: "fullscreen" };
      case "KeyM":
        return shift ? null : { type: "palette" };
      case "KeyK":
        return shift ? null : { type: "shortcuts" };
      case "KeyZ":
        return shift ? { type: "redo" } : { type: "undo" };
      case "KeyB":
        return shift ? null : { type: "alfred" };
      case "KeyY":
        return { type: "rotatePhoto", direction: shift ? -1 : 1 };
      default:
        return null;
    }
  }

  /* F11 no se toca: es la pantalla completa del navegador, y la pantalla la
     nota sola (`use-fullscreen.ts`). */
  if (!alt && !ctrl && !editing) {
    if (press.key === "Enter" && !shift && free && press.repeat !== true) {
      return { type: "save" };
    }
    if (press.key === "?") return { type: "shortcuts" };
    if (press.key === "/") return { type: "search" };
    /* Esc fuera de un campo: lo que no lo atendió nadie antes --un campo,
       un menú, un recorte, la lista-- cierral documento. */
    if (press.key === "Escape" && !shift) return { type: "closeInvoice" };
  }
  return null;
};

/** Una combinación, tecla por tecla: `["Alt", "Shift", "1"]`. */
export type Keys = readonly string[];

/** Los modificadores de la Mac, en el orden de Apple. */
const MAC_MODIFIERS = ["⌃", "⌥", "⇧", "⌘"] as const;

/** Las teclas que en la Mac se dibujan con su símbolo. */
const MAC_NAMES: Record<string, string> = { Enter: "↩", Backspace: "⌫" };

/**
 * Una combinación del mapa, como se ve en esta plataforma. En un PC, tal
 * cual. En Mac, la traducción de `fromMac` al revés --Alt es ⌃, salvo con ↑
 * y ↓, que es ⌥; Ctrl es ⌘-- con los símbolos y en el orden de Apple
 * (⌃⌥⇧⌘): `["Alt", "Shift", "1"]` es `["⌃", "⇧", "1"]`. F11 es ⌃⌘F, la
 * pantalla completa de las apps de la Mac (Apple, «Mac keyboard
 * shortcuts»): en Mac, F11 muestra el escritorio.
 */
export const keysFor = (keys: Keys, platform: Platform = PLATFORM): Keys => {
  if (platform === "pc") return keys;
  if (keys.length === 1 && keys[0] === "F11") return ["⌃", "⌘", "F"];
  const last = keys.at(-1);
  const vertical = last === "↑" || last === "↓";
  const held = new Set<string>();
  const rest: string[] = [];
  for (const key of keys) {
    if (key === "Alt") held.add(vertical ? "⌥" : "⌃");
    else if (key === "Ctrl") held.add("⌘");
    else if (key === "Shift") held.add("⇧");
    else rest.push(MAC_NAMES[key] ?? key);
  }
  return [...MAC_MODIFIERS.filter((key) => held.has(key)), ...rest];
};

/**
 * Una combinación como texto, para un `title` o una frase: «Alt+Shift+N» en
 * un PC, «⌃⇧N» en Mac, que junta los símbolos como Apple.
 */
export const keysLabel = (keys: Keys, platform: Platform = PLATFORM): string =>
  keysFor(keys, platform).join(platform === "mac" ? "" : "+");

/**
 * Las insignias de los botones. Son las mismas combinaciones que entiende
 * `commandOf`; la prueba del mapa comprueba que no se separen.
 */
export const KEYS = {
  save: ["Ctrl", "Enter"],
  valid: ["Alt", "Shift", "1"],
  invalid: ["Alt", "Shift", "2"],
  forData: ["Alt", "Shift", "3"],
  takeNext: ["Alt", "Enter"],
  closeInvoice: ["Esc"],
  release: ["Alt", "Shift", "Enter"],
  undo: ["Alt", "Z"],
  redo: ["Alt", "Shift", "Z"],
  undoWeb: ["Ctrl", "Z"],
  redoWeb: ["Ctrl", "Shift", "Z"],
  queue: ["Alt", "0"],
  search: ["/"],
  documentData: ["Alt", "1"],
  items: ["Alt", "2"],
  up: ["Alt", "↑"],
  down: ["Alt", "↓"],
  next: ["Alt", "S"],
  previous: ["Alt", "Shift", "S"],
  addLine: ["Alt", "N"],
  removeLine: ["Alt", "Shift", "N"],
  notProduct: ["Alt", "C"],
  demoteLine: ["Alt", "Shift", "C"],
  batch: ["Alt", "L"],
  attention: ["Alt", "A"],
  attentionBack: ["Alt", "Shift", "A"],
  full: ["Alt", "T"],
  fullAll: ["Alt", "Shift", "T"],
  split: ["Alt", "J"],
  boxes: ["Alt", "R"],
  zoomIn: ["Alt", "+"],
  zoomOut: ["Alt", "-"],
  original: ["Alt", "O"],
  fullscreen: ["Alt", "P"],
  fullWidth: ["Alt", "Shift", "P"],
  palette: ["Alt", "M"],
  paletteWeb: ["Ctrl", "K"],
  shortcuts: ["Alt", "K"],
  shortcutsAlone: ["?"],
  alfred: ["Alt", "B"],
  rotate: ["Alt", "Y"],
  rotateBack: ["Alt", "Shift", "Y"],
} as const satisfies Record<string, Keys>;

export const SECTION_KEYS: Record<SectionKey, Keys> = {
  documentData: KEYS.documentData,
  items: KEYS.items,
};

/**
 * Confirmar lo elegido: Enter sobre el botón, que tiene el foco, o fuera de
 * todo lo que atiende Enter por su cuenta.
 */
export const CONFIRM_KEYS: Keys = ["Enter"];

export const DECISION_KEYS: Record<DecisionKind, Keys> = {
  valid: KEYS.valid,
  invalid: KEYS.invalid,
  forData: KEYS.forData,
};

export interface ShortcutRow {
  /** Las combinaciones que hacen lo mismo. */
  readonly keys: readonly Keys[];
  readonly label: string;
}

export interface ShortcutGroup {
  readonly title: string;
  readonly rows: readonly ShortcutRow[];
}

/** La gramática, dicha para la chuleta. */
export const GRAMMAR: readonly ShortcutRow[] = [
  { keys: [["Alt", "letra"]], label: "Hace algo: la letra es su inicial" },
  {
    keys: [["Enter"]],
    label:
      "Fuera de un campo, un botón o la lista: confirma lo elegido y pasa a la siguiente. Clic, clic, clic",
  },
  {
    keys: [["Alt", "Enter"]],
    label: "Toma la siguiente factura: la acción principal",
  },
  {
    keys: [["Alt", "número"]],
    label: "Vas tú: 0 la cola, 1 y 2 los recortes",
  },
  {
    keys: [["Alt", "Shift", "número"]],
    label: "Va la factura: 1 Válida, 2 Inválida, 3 Para información",
  },
  {
    keys: [["Alt", "Shift", "letra"]],
    label: "La otra cara del mismo gesto: de uno a todos, o al revés",
  },
  {
    keys: [KEYS.undo, KEYS.redo],
    label:
      "Deshacer y rehacer: la Z de todos los editores, y Shift la otra cara",
  },
];

/** La chuleta, por grupos, en el orden en que se aprende. */
export const SHORTCUT_GROUPS: readonly ShortcutGroup[] = [
  {
    title: "El trabajo",
    rows: [
      {
        keys: [KEYS.takeNext],
        label: "Tomar siguiente: el más antiguo que nadie tiene tomado",
      },
      {
        keys: [KEYS.release],
        label: "Soltar el que tienes, para que lo pueda tomar otra persona",
      },
      { keys: [KEYS.save], label: "Guarda la decisión y toma la siguiente" },
      {
        keys: [KEYS.undo, KEYS.undoWeb],
        label:
          "Deshacer lo último que se guardó --decidir, agregar o quitar una línea-- mientras se ve el botón. La decisión espera unos segundos antes de salir",
      },
      {
        keys: [KEYS.redo, KEYS.redoWeb],
        label: "Rehacer lo deshecho, mientras se ve el botón",
      },
      {
        keys: [KEYS.closeInvoice],
        label:
          "Fuera de un campo: cerrar la factura. La factura se suelta si es tuya y no la decidiste; con cambios sin guardar, pregunta",
      },
      {
        keys: [KEYS.valid, KEYS.invalid, KEYS.forData],
        label: "Válida, Inválida o Para información, y toma el siguiente",
      },
      {
        keys: [CONFIRM_KEYS, KEYS.save],
        label:
          "Confirmar la decisión elegida y pasar al siguiente. Sin corregir nada, van los datos tal como se leyeron",
      },
      {
        keys: [["1"], ["2"], ["3"]],
        label: "En la barra de decisión: elige sin guardar",
      },
      {
        keys: [["1–9"]],
        label: "El motivo de la inválida, con la lista abierta",
      },
    ],
  },
  {
    title: "Los datos (dentro de los campos)",
    rows: [
      {
        keys: [["Tab"], ["Enter"]],
        label: "Acepta el valor y pasa al siguiente",
      },
      {
        keys: [
          ["Shift", "Tab"],
          ["Shift", "Enter"],
        ],
        label: "Vuelve al anterior",
      },
      { keys: [["Esc"]], label: "Devuelve el valor sugerido" },
    ],
  },
  {
    title: "Las líneas",
    rows: [
      {
        keys: [KEYS.addLine],
        label: "Agrega una línea que la lectura no vio, con su descripción",
      },
      { keys: [["Esc"]], label: "En la línea nueva: la descarta" },
      {
        keys: [KEYS.removeLine],
        label:
          "Quita una línea agregada a mano; las de la lectura no se quitan",
      },
      {
        keys: [KEYS.notProduct],
        label: "Pliega o despliega «No participan» / «Otros ítems»",
      },
      {
        keys: [KEYS.batch],
        label:
          "Confirmar en lote: la vista previa de las facturas pendientes, y Enter",
      },
      {
        keys: [KEYS.attention, KEYS.attentionBack],
        label: "Al siguiente dato o producto que pide atención, o al anterior",
      },
      {
        keys: [KEYS.demoteLine],
        label:
          "Baja la línea con el foco a «No participa»: se guarda al momento, como en el selector",
      },
    ],
  },
  {
    title: "Moverse",
    rows: [
      {
        keys: [KEYS.documentData, KEYS.items],
        label: "Al recorte de los datos de la factura o de las líneas",
      },
      {
        keys: [KEYS.queue, KEYS.search],
        label: "Al buscador de la cola",
      },
      {
        keys: [KEYS.up, KEYS.down],
        label:
          "Al panel de arriba o de abajo: del dato a su recorte, y de vuelta",
      },
      {
        keys: [KEYS.next, KEYS.previous],
        label: "Factura siguiente o anterior de la lista",
      },
    ],
  },
  {
    title: "La cola",
    rows: [
      { keys: [["Enter"]], label: "En el buscador: busca lo escrito" },
      {
        keys: [["Backspace"]],
        label: "Con el buscador vacío: quita el último chip",
      },
      { keys: [["↓"]], label: "Del buscador a la lista" },
      { keys: [["↑"], ["↓"]], label: "En la lista: la recorren" },
      { keys: [["Enter"]], label: "En la lista: abre la factura" },
      { keys: [["número"]], label: "En la lista: salta a esa posición" },
      { keys: [["Esc"]], label: "Vuelve a los datos" },
    ],
  },
  {
    title: "La foto",
    rows: [
      {
        keys: [KEYS.full],
        label: "Foto completa o recorte, en el recorte activo",
      },
      { keys: [KEYS.fullAll], label: "Lo mismo, en los tres a la vez" },
      {
        keys: [KEYS.split],
        label:
          "Cambia la división: el recorte activo a todo el ancho, o los tres",
      },
      {
        keys: [KEYS.boxes],
        label: "Muestra u oculta los recuadros, para toda la sesión",
      },
      {
        keys: [KEYS.zoomIn, KEYS.zoomOut],
        label: "Acerca o aleja el recorte activo",
      },
      {
        keys: [KEYS.original],
        label: "La foto o el PDF tal como llegó, encima de todo",
      },
      {
        keys: [KEYS.rotate, KEYS.rotateBack],
        label:
          "Gira la foto un cuarto de vuelta, horario o antihorario: se guarda sola",
      },
    ],
  },
  {
    title: `Dentro de un recorte (con ${keysLabel(KEYS.documentData)} o 2)`,
    rows: [
      { keys: [["←"], ["↑"], ["→"], ["↓"]], label: "Desplaza la foto" },
      { keys: [["+"], ["-"]], label: "Acerca o aleja" },
      { keys: [["0"]], label: "Vuelve al encuadre" },
      { keys: [["T"]], label: "Foto completa o recorte" },
      { keys: [["Esc"], ["Enter"]], label: "Vuelve a los datos" },
    ],
  },
  {
    title: "La pantalla",
    rows: [
      { keys: [KEYS.fullscreen, ["F11"]], label: "Pantalla completa" },
      {
        keys: [KEYS.fullWidth],
        label: "Ancho completo: esconde la cola de facturas",
      },
      { keys: [KEYS.palette, KEYS.paletteWeb], label: "El menú de comandos" },
      {
        keys: [KEYS.shortcuts, KEYS.shortcutsAlone],
        label: "Esta lista de atajos",
      },
      { keys: [KEYS.alfred], label: "Cuéntale a Alfred lo que necesitas" },
    ],
  },
];

/** Lo que ofrece el menú de comandos, con su atajo para que se aprenda. */
export interface PaletteEntry {
  readonly command: Command;
  readonly label: string;
  readonly keys?: Keys;
}

export const PALETTE_ENTRIES: readonly PaletteEntry[] = [
  {
    command: { type: "takeNext" },
    label: "Tomar siguiente",
    keys: KEYS.takeNext,
  },
  {
    command: { type: "release" },
    label: "Soltar esta factura",
    keys: KEYS.release,
  },
  {
    command: { type: "closeInvoice" },
    label: "Cerrar la factura",
    keys: KEYS.closeInvoice,
  },
  { command: { type: "undo" }, label: "Deshacer", keys: KEYS.undo },
  { command: { type: "redo" }, label: "Rehacer", keys: KEYS.redo },
  { command: { type: "save" }, label: "Guardar y seguir", keys: KEYS.save },
  {
    command: { type: "decide", kind: "valid", apply: true },
    label: "Válida y seguir",
    keys: KEYS.valid,
  },
  {
    command: { type: "decide", kind: "invalid", apply: true },
    label: "Inválida…",
    keys: KEYS.invalid,
  },
  {
    command: { type: "decide", kind: "forData", apply: true },
    label: "Para información y seguir",
    keys: KEYS.forData,
  },
  {
    command: { type: "search" },
    label: "Buscar una factura",
    keys: KEYS.search,
  },
  {
    command: { type: "invoice", step: 1 },
    label: "Factura siguiente",
    keys: KEYS.next,
  },
  {
    command: { type: "invoice", step: -1 },
    label: "Factura anterior",
    keys: KEYS.previous,
  },
  {
    command: { type: "goToCrop", section: "documentData" },
    label: "Ir al recorte de los datos de la factura",
    keys: KEYS.documentData,
  },
  {
    command: { type: "goToCrop", section: "items" },
    label: "Ir al recorte de las líneas",
    keys: KEYS.items,
  },
  {
    command: { type: "addLine" },
    label: "Agregar una línea",
    keys: KEYS.addLine,
  },
  {
    command: { type: "removeLine" },
    label: "Quitar línea",
    keys: KEYS.removeLine,
  },
  {
    command: { type: "toggleNotProduct" },
    label: "Plegar o desplegar «No participan» / «Otros ítems»",
    keys: KEYS.notProduct,
  },
  {
    command: { type: "demoteLine" },
    label: "Bajar la línea con el foco a «No participa»",
    keys: KEYS.demoteLine,
  },
  {
    command: { type: "batch" },
    label: "Confirmar en lote",
    keys: KEYS.batch,
  },
  {
    command: { type: "attention", step: 1 },
    label: "Al siguiente dato o producto que pide atención",
    keys: KEYS.attention,
  },
  {
    command: { type: "attention", step: -1 },
    label: "Al anterior dato o producto que pide atención",
    keys: KEYS.attentionBack,
  },
  {
    command: { type: "toggleFull", all: false },
    label: "Foto completa o recorte",
    keys: KEYS.full,
  },
  {
    command: { type: "toggleFull", all: true },
    label: "Foto completa o recorte, en los tres",
    keys: KEYS.fullAll,
  },
  {
    command: { type: "toggleSplit" },
    label: "Cambiar la división",
    keys: KEYS.split,
  },
  {
    command: { type: "toggleBoxes" },
    label: "Mostrar u ocultar los recuadros",
    keys: KEYS.boxes,
  },
  {
    command: { type: "zoom", direction: 1 },
    label: "Acercar",
    keys: KEYS.zoomIn,
  },
  {
    command: { type: "zoom", direction: -1 },
    label: "Alejar",
    keys: KEYS.zoomOut,
  },
  {
    command: { type: "original" },
    label: "Ver la foto o el PDF tal como llegó",
    keys: KEYS.original,
  },
  {
    command: { type: "rotatePhoto", direction: 1 },
    label: "Girar la foto en sentido horario",
    keys: KEYS.rotate,
  },
  {
    command: { type: "rotatePhoto", direction: -1 },
    label: "Girar la foto en sentido antihorario",
    keys: KEYS.rotateBack,
  },
  {
    command: { type: "fullscreen" },
    label: "Pantalla completa",
    keys: KEYS.fullscreen,
  },
  {
    command: { type: "fullWidth" },
    label: "Ancho completo",
    keys: KEYS.fullWidth,
  },
  {
    command: { type: "shortcuts" },
    label: "Atajos de teclado",
    keys: KEYS.shortcuts,
  },
  {
    command: { type: "alfred" },
    label: "Cuéntale a Alfred lo que necesitas",
    keys: KEYS.alfred,
  },
  { command: { type: "alfredList" }, label: "Ver los pedidos a Alfred" },
  /* Con el modo Chat apagado no se ofrece ir a él (`CHAT_MODE_ENABLED`). */
  ...(CHAT_MODE_ENABLED
    ? [
        {
          command: { type: "mode", mode: "chat" },
          label: "Ir al modo Chat",
        } as const,
      ]
    : []),
  { command: { type: "mode", mode: "work" }, label: "Ir al modo Work" },
];
